import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Filter, Check, Mail } from 'lucide-react';
import { ageBooks, ageCategories } from '../data/mock';

const StartHerePage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedAges, setSelectedAges] = useState([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Filter to show only ages 4-12
  const availableBooks = ageBooks.filter(book => book.age <= 12);
  const futureAges = [13, 14, 15, 16, 17, 18];

  const filteredBooks = selectedCategory === 'all' 
    ? availableBooks 
    : availableBooks.filter(book => {
        const category = Object.values(ageCategories).find(cat => cat.ages.includes(book.age));
        return category && category.label.toLowerCase().includes(selectedCategory);
      });

  const toggleAge = (age) => {
    setSelectedAges(prev => 
      prev.includes(age) ? prev.filter(a => a !== age) : [...prev, age]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedAges.length === 0) return;
    
    setSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/age-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: name || null,
          requested_ages: selectedAges,
          message: message || null
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setShowRequestForm(false);
          setSelectedAges([]);
          setEmail('');
          setName('');
          setMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--gradient-hero-subtle)', paddingTop: '4rem' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-hero mb-4">Choose Child's Age</h1>
            <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
              Select your child's current age to find the right book. Each curriculum is specifically designed 
              for that year of development.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="flex items-center gap-4 mb-8 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter size={20} />
              <span className="body-small font-medium">Filter by stage:</span>
            </div>
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                selectedCategory === 'all' 
                  ? 'text-white shadow-md' 
                  : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              style={selectedCategory === 'all' ? { background: 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)' } : {}}
            >
              All Ages
            </button>
            <button
              onClick={() => setSelectedCategory('early')}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                selectedCategory === 'early' 
                  ? 'text-white shadow-md' 
                  : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              style={selectedCategory === 'early' ? { background: 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)' } : {}}
            >
              Early Start (2-3)
            </button>
            <button
              onClick={() => setSelectedCategory('foundations')}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                selectedCategory === 'foundations' 
                  ? 'text-white shadow-md' 
                  : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              style={selectedCategory === 'foundations' ? { background: 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)' } : {}}
            >
              Foundations (4-7)
            </button>
            <button
              onClick={() => setSelectedCategory('communication')}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                selectedCategory === 'communication' 
                  ? 'text-white shadow-md' 
                  : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              style={selectedCategory === 'communication' ? { background: 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)' } : {}}
            >
              Communication (8-12)
            </button>
          </div>

          {/* Books Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredBooks.map((book) => (
              <Link 
                key={book.age}
                to={`/book/${book.age}`}
                className={`age-card ${book.color}`}
                style={{ position: 'relative' }}
              >
                {book.age >= 11 && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: 'var(--text-primary)',
                    border: '1px solid rgba(91, 127, 255, 0.3)',
                    padding: '0.25rem 0.625rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.625rem',
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    zIndex: 10,
                    opacity: 0.85
                  }}>
                    Coming Soon
                  </div>
                )}
                <div className="flex items-center justify-center w-20 h-20 rounded-full mb-4 mx-auto" style={{ background: 'rgba(255, 255, 255, 0.6)' }}>
                  <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                    {book.age}
                  </span>
                </div>
                <h3 className="heading-3 text-center mb-2">{book.title}</h3>
                <p className="body-small text-center mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {book.focus}
                </p>
                <div className="mt-auto pt-4 border-t border-border-light">
                  <div className="flex flex-col gap-2">
                    <span className="btn-secondary text-xs py-2 px-4 text-center w-full block">View Details</span>
                    {book.age <= 10 && (
                      <a 
                        href={book.amazonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary text-xs py-2 px-4 text-center w-full block"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Buy Now
                      </a>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Request Additional Ages Section */}
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="card text-center">
              <h2 className="heading-2 mb-4">Looking for Ages 13-18?</h2>
              <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                We're working on expanding our curriculum to cover teen years. 
                Let us know which ages you're interested in and we'll notify you when they're available.
              </p>
              
              {!showRequestForm ? (
                <button 
                  onClick={() => setShowRequestForm(true)}
                  className="btn-primary"
                >
                  Request Teen Ages (13-18)
                </button>
              ) : submitted ? (
                <div className="py-8">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
                    <Check size={32} className="text-green-600" />
                  </div>
                  <h3 className="heading-3 mb-2">Thank You!</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    We've received your request and will notify you when these ages become available.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="text-left">
                  <div className="mb-6">
                    <label className="body-small font-medium mb-3 block text-center">Select Ages You're Interested In *</label>
                    <div className="flex flex-wrap gap-3 justify-center">
                      {futureAges.map(age => (
                        <button
                          key={age}
                          type="button"
                          onClick={() => toggleAge(age)}
                          className={`w-14 h-14 rounded-full font-bold text-lg transition-all ${
                            selectedAges.includes(age)
                              ? 'text-white shadow-md'
                              : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                          }`}
                          style={selectedAges.includes(age) ? { background: 'linear-gradient(135deg, #5B7FFF 0%, #A78BFA 100%)' } : {}}
                        >
                          {age}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="body-small font-medium mb-2 block">Email *</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none"
                        style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                      />
                    </div>
                    <div>
                      <label className="body-small font-medium mb-2 block">Name (optional)</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none"
                        style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="body-small font-medium mb-2 block">Message (optional)</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Any specific needs or questions about the teen curriculum?"
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none resize-none"
                      style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                    />
                  </div>

                  <div className="flex gap-3 justify-center">
                    <button
                      type="submit"
                      disabled={submitting || selectedAges.length === 0}
                      className="btn-primary flex items-center gap-2"
                      style={{ opacity: (submitting || selectedAges.length === 0) ? 0.6 : 1 }}
                    >
                      <Mail size={18} />
                      {submitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRequestForm(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Help Box */}
          <div className="mt-12 max-w-2xl mx-auto card" style={{ background: 'var(--accent-blue-200)' }}>
            <h3 className="heading-3 mb-3">Not sure where to start?</h3>
            <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
              <li>• <strong>Start at your child's current age.</strong> Each book is built for that developmental stage.</li>
              <li>• <strong>Use flexible pacing.</strong> Go at your child's pace—daily practice or weekly focus.</li>
              <li>• <strong>Reinforce earlier skills as needed.</strong> You can always reference tools from younger ages.</li>
            </ul>
            <div className="mt-4">
              <Link to="/faq" className="btn-primary text-sm">
                Read More FAQs
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StartHerePage;
